import { action, computed, observable, makeObservable } from 'mobx';
import { Canceler } from 'axios';
import moment from 'moment';
import { defaultFoodicsLogFilters } from '../constants/foodicsLogRow';
import { defaultPagination } from 'src/constants/main';
import { IPagination, LoadingStatus } from 'src/types/main';
import { IFoodicsLogFilters } from 'src/view/screens/FoodicsLogScreen/screens/FoodicsLog/types/foodicsLog';
import client from 'src/services/client';
import { getCancelToken } from 'src/utils/helpers';
import logger from '~/services/logger';
import { dateFormatForRequest } from '~/constants/dateTime';
import { localeStore } from '~/mobx/localesStore';

export interface IChangeDetail {
  after: any;
  before: any;
}

export type IChanges = Record<string, IChangeDetail | undefined>;

export interface IFoodicsLog {
  id: number;
  client_pos_system_id: number;
  user_id: number;
  action: number;
  sync_data: any;
  synced_from: string | null;
  daily_sync: number;
  created_at: string;
  updated_at: string;
  userName: string;
  syncDataText: string;
  actionName: string;
}

class FoodicsLogStore {
  constructor() {
    makeObservable(this);
  }

  @observable pagination: IPagination<IFoodicsLog> = defaultPagination;
  @observable filters: IFoodicsLogFilters = defaultFoodicsLogFilters;
  @observable public exportLoadingStatus: LoadingStatus = LoadingStatus.Init;

  FoodicsLogExportAbortController = new AbortController();

  cancelStack: {
    [key: string]: Canceler;
  } = {};

  /**
   * Are there no existed foodics in system
   */
  get isEmptyFoodicsLogList() {
    return !this.pagination.loading && !this.pagination.data.length;
  }

  get countLines() {
    return this.pagination.data.length;
  }

  @computed get isLoading(): boolean {
    return Boolean(this.pagination.loading || this.pagination.refreshing);
  }

  @action setFilters = (filters: Partial<IFoodicsLogFilters>) => {
    this.filters = { ...this.filters, ...filters };
    this.fetchFoodicsLogList();
  };

  @action fetchFoodicsLogList = async (page: number = 1, initLoading: boolean = true) => {
    try {
      const cancelId = 'fetchFoodicsLogList';
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      // skip loading if its already initialized
      if (initLoading) {
        this.pagination.loading = true;
        this.pagination.refreshing = true;
      }

      const pagination = await client.get('v3/foodics/log', {
        params: {
          page,
          ...this.getParams(),
        },
        headers: { 'Accept-Language': localeStore.language },
        cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)),
      });

      this.pagination = {
        ...pagination,
        loading: false,
        refreshing: false,
      };
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination.loading = false;
      this.pagination.refreshing = false;
      logger.error({ error, place: 'FoodicsLogStore fetchFoodicsLogList' });
    }
  };

  clear = () => {
    this.pagination = defaultPagination;
    this.filters = defaultFoodicsLogFilters;
  };

  private getParams = () => {
    return {
      filters: {
        // At the last moment, business requirements changed and these parameters were overboard.
        // They are sent to the backend but not used.
        date_start: this.formatDate(this.filters.startDate),
        date_end: this.formatDate(this.filters.endDate),
      },
    };
  };

  public formatDate = (date: number) => {
    return moment(date * 1000).format(dateFormatForRequest);
  };
}

const foodicsLogStore = new FoodicsLogStore();

export default foodicsLogStore;
